// ─── BattleViewer2D – Animation Queue Builder & Sprite Initialisation ───────

import { parseCombatTag } from '../../../../utils/combatLogUtils';
import {
  AnimEvent, Sprite, ParticipantSummary,
  CANVAS_W, CANVAS_H, ARENA_INSET, CIRCLE_RADIUS,
  IDLE_SPEED, DAMAGE_EVENTS, LEFT_TEAMS, RIGHT_TEAMS,
} from './types';
import { resolveText, getLogStyle } from './utils';

export function buildAnimQueue(
  combatLog: string[],
  summaries: Record<string, ParticipantSummary>,
): AnimEvent[] {
  const zeroDmgHits: Record<string, number> = {};
  const trackedDmg:  Record<string, number> = {};
  for (const line of combatLog) {
    const { eventType, meta } = parseCombatTag(line);
    if (!eventType || !DAMAGE_EVENTS.has(eventType)) continue;
    const tKey = String(meta.target ?? '');
    if (!tKey) continue;
    const dmg = Number(meta.damage ?? 0);
    if (dmg > 0) trackedDmg[tKey] = (trackedDmg[tKey] || 0) + dmg;
    else zeroDmgHits[tKey] = (zeroDmgHits[tKey] || 0) + 1;
  }
  const perEventEst: Record<string, number> = {};
  for (const key of Object.keys(zeroDmgHits)) {
    const total = summaries[key]?.damageTaken ?? 0;
    const tracked = trackedDmg[key] ?? 0;
    const unaccounted = Math.max(0, total - tracked);
    perEventEst[key] = zeroDmgHits[key] > 0
      ? Math.max(1, Math.round(unaccounted / zeroDmgHits[key]))
      : 0;
  }

  return combatLog.map(line => {
    const { eventType, text, meta } = parseCombatTag(line);
    const { color, bold } = getLogStyle(eventType);
    const targetKey = String(meta.target ?? '');
    let damage = Number(meta.damage ?? 0);

    if (eventType === 'hospital' && !meta.actor) {
      const plainText = resolveText(text);
      for (const [key, summary] of Object.entries(summaries)) {
        if (plainText.startsWith(summary.name + ' ')) { meta.actor = key; break; }
      }
    }

    if (damage === 0 && eventType && DAMAGE_EVENTS.has(eventType) && targetKey) {
      damage = perEventEst[targetKey] ?? 0;
    }

    let round = Number(meta.round ?? 0);
    if (eventType === 'round_header' && round === 0) {
      const m = text.match(/\d+/);
      if (m) round = parseInt(m[0], 10);
    }

    return {
      eventType: eventType ?? 'unknown',
      actorKey: String(meta.actor ?? ''),
      targetKey,
      damage, round,
      text: resolveText(text),
      logColor: color, logBold: bold,
      bodyPart: meta.bodyPart ? String(meta.bodyPart) : undefined,
      slot: meta.slot ? String(meta.slot) : undefined,
    };
  });
}

export function initSprites(
  summaries: Record<string, ParticipantSummary>,
): Map<string, Sprite> {
  const sprites = new Map<string, Sprite>();
  const leftEntries  = Object.entries(summaries).filter(([, p]) => LEFT_TEAMS.includes(p.team));
  const rightEntries = Object.entries(summaries).filter(([, p]) => RIGHT_TEAMS.includes(p.team));
  const totalCount = leftEntries.length + rightEntries.length;
  const scaleAdj = totalCount > 8 ? Math.max(0.8, 1 - (totalCount - 8) * 0.03) : 1;

  const placeTeam = (entries: [string, ParticipantSummary][], xMin: number, xMax: number) => {
    const count = entries.length;
    const vertStep = Math.floor(
      (CANVAS_H - ARENA_INSET * 2 - CIRCLE_RADIUS * 4) / Math.max(count, 1),
    );
    const totalH = vertStep * count;
    const topY = ARENA_INSET + CIRCLE_RADIUS * 2 +
      Math.floor((CANVAS_H - ARENA_INSET * 2 - totalH) / 2);

    entries.forEach(([key, p], i) => {
      const x = Math.round(xMin + Math.random() * (xMax - xMin));
      const y = Math.round(topY + i * vertStep + (Math.random() * 20 - 10));
      const maxHp = (p.maxHP || 0) > 0
        ? p.maxHP
        : Math.max(1, (p.finalHP ?? 0) + (p.damageTaken ?? 0));
      const startingHp = p.startingHP != null && p.startingHP > 0 ? p.startingHP : maxHp;

      sprites.set(key, {
        key, name: p.name ?? key, team: p.team,
        x, y, baseX: x, baseY: y,
        vx: (Math.random() - 0.5) * IDLE_SPEED,
        vy: (Math.random() - 0.5) * IDLE_SPEED,
        state: 'idle', stateProgress: 0,
        stateTargetX: 0, stateTargetY: 0,
        stateOriginX: x, stateOriginY: y,
        hp: startingHp, maxHp,
        flashTimer: 0, flashColor: '#fff',
        opacity: 1,
        race: p.race ?? '', sex: p.sex ?? '',
        avatarNum: p.avatar ?? 0,
        isLegend: p.isLegend ?? false,
        hospitalized: false,
        scale: scaleAdj, shakeX: 0, shakeY: 0, rotation: 0,
        weaponType: p.weaponType ?? '', offHandType: p.offHandType ?? '',
        hasShield: p.hasShield ?? false,
        comboCount: 0, comboTimer: 0,
        auraColor: '', auraTimer: 0,
        breathPhase: Math.random() * Math.PI * 2,
        weaponBroken: false,
        offHandBroken: false,
        shieldBroken: false,
      });
    });
  };

  placeTeam(leftEntries,  Math.floor(CANVAS_W * 0.32), Math.floor(CANVAS_W * 0.44));
  placeTeam(rightEntries, Math.floor(CANVAS_W * 0.56), Math.floor(CANVAS_W * 0.68));
  return sprites;
}
